import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Sidebar } from './Sidebar';
import { Breadcrumbs } from './Breadcrumbs';
import { useIsMobile } from '../../hooks/useMediaQuery';
import '../../styles/page-layout.css';

interface BreadcrumbItem {
  label: string;
  href?: string;
}

interface PageLayoutProps {
  breadcrumbItems: BreadcrumbItem[];
  children: React.ReactNode;
  actions?: React.ReactNode;
  /** Drops the 16px padding around <main> (boards and other edge-to-edge views). */
  mainNoPadding?: boolean;
  /** Overrides the mobile top-bar title; defaults to the last breadcrumb. */
  title?: string;
}

export const PageLayout: React.FC<PageLayoutProps> = ({
  breadcrumbItems,
  children,
  actions,
  mainNoPadding = false,
  title,
}) => {
  const isMobile = useIsMobile();
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();

  const closeNav = useCallback(() => setNavOpen(false), []);

  // Navigating away always dismisses the drawer
  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  // The drawer is an overlay — freeze the page behind it
  useEffect(() => {
    if (!navOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [navOpen]);

  useEffect(() => {
    if (!navOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setNavOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [navOpen]);

  // Any link inside the drawer navigates, so the drawer should get out of the way.
  // Buttons (new board, account settings) open modals and must leave it alone.
  const handleDrawerClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!isMobile) return;
    const link = (event.target as HTMLElement).closest('a[href]');
    if (link) setNavOpen(false);
  };

  const barTitle = title ?? breadcrumbItems[breadcrumbItems.length - 1]?.label ?? '';

  return (
    <div className="page-layout">
      <header className="page-layout__topbar">
        <button
          type="button"
          className="page-layout__menu-btn"
          onClick={() => setNavOpen(true)}
          aria-label="Open navigation"
          aria-expanded={navOpen}
          aria-controls="app-nav"
        >
          <Menu size={22} />
        </button>
        <span className="page-layout__topbar-title">{barTitle}</span>
        {actions && <div className="page-layout__topbar-actions">{actions}</div>}
      </header>

      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
      <div id="app-nav" onClick={handleDrawerClick}>
        <Sidebar
          className={`app-nav ${navOpen ? 'app-nav--open' : ''}`}
          header={
            <button
              type="button"
              className="sidebar__close-btn"
              onClick={closeNav}
              aria-label="Close navigation"
            >
              <X size={20} />
            </button>
          }
        />
      </div>

      {navOpen && (
        <div
          className="page-layout__scrim"
          onClick={closeNav}
          role="presentation"
        />
      )}

      <div className="page-layout__content">
        <Breadcrumbs items={breadcrumbItems} actions={actions} />
        <main className={`page-layout__main ${mainNoPadding ? 'page-layout__main--no-padding' : ''}`}>
          {children}
        </main>
      </div>
    </div>
  );
};
